import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { UserCog, Plus, Loader2, Ban, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BASE_ROLES = ["accountant", "staff", "student", "parent"];

export default function Users() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const roleOptions = isSuper ? ["super_admin", "admin", ...BASE_ROLES] : BASE_ROLES;
  const isAdminRole = (r) => r === "admin" || r === "super_admin";
  const [users, setUsers] = useState(null);
  const [catalog, setCatalog] = useState({ all: [], role_defaults: {} });
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ name: "", username: "", password: "", phone: "", role: "staff", permissions: [], student_ids: [] });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/permissions/catalog").then((r) => setCatalog(r.data));
    api.get("/students", { params: { limit: 100 } }).then((r) => setStudents(r.data.students)).catch(() => {});
  }, []);

  const openNew = () => { setEdit(null); setF({ name: "", username: "", password: "", phone: "", role: "staff", permissions: catalog.role_defaults.staff || [], student_ids: [] }); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setF({ name: u.name, username: u.username, password: "", phone: u.phone || "", role: u.role, permissions: u.permissions || [], student_ids: u.student_ids || [] }); setOpen(true); };

  const setRole = (role) => setF({ ...f, role, permissions: catalog.role_defaults[role] || [] });
  const togglePerm = (p) => setF({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] });
  const toggleStudent = (id) => setF({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter((x) => x !== id) : [...f.student_ids, id] });

  const save = async () => {
    try {
      if (edit) { const payload = { ...f }; if (!payload.password) delete payload.password; await api.put(`/users/${edit.id}`, payload); }
      else await api.post("/users", f);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleDisable = async (u) => {
    await api.put(`/users/${u.id}`, { status: u.status === "Active" ? "Disabled" : "Active" });
    toast.success("Updated"); load();
  };

  if (!users) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Users & Permissions</h1><p className="text-slate-500">{users.length} accounts</p></div>
        <Button onClick={openNew} data-testid="add-user-btn"><Plus size={18} className="mr-2" />Add User</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Username</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{u.username}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-xs capitalize">{u.role}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "Active" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>{u.status}</span></td>
                <td className="px-4 py-3 text-right flex gap-1 justify-end">
                  {(!isAdminRole(u.role) || isSuper) ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className={u.status === "Active" ? "text-rose-500" : "text-emerald-500"} onClick={() => toggleDisable(u)}>{u.status === "Active" ? <Ban size={14} /> : <CheckCircle2 size={14} />}</Button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 flex items-center gap-1 pr-2"><ShieldCheck size={13} />Super Admin only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label>Username</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} disabled={!!edit} data-testid="user-username" /></div>
            <div><Label>Password {edit && <span className="text-xs text-slate-400">(leave blank to keep)</span>}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div><Label>Role</Label><Select value={f.role} onValueChange={setRole}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
          </div>

          {["student", "parent"].includes(f.role) && (
            <div>
              <Label>Linked Student(s)</Label>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 grid grid-cols-2 gap-1 mt-1">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={f.student_ids.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} data-testid={`link-student-${s.admission_number}`} />
                    {s.name} <span className="text-xs text-slate-400">({s.admission_number})</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {!isAdminRole(f.role) && (
            <div>
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {catalog.all.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={f.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                    {p.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="save-user-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
